import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Audit from "src/components/Audit";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import LovDataOrigin from "src/components/Lov/DataOrigin";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { IComponentIntegrations, IEftba011, IRefIntegrations } from "./interface";
import addAndChangeSchema from "./schemas";
// import addAndChangeSchema from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefIntegrations, IComponentIntegrations> = ({
	paginationRef
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { confirm } = useConfirmModal();
	const { setLoading } = useLoaderBackdrop();
	const [selectedIntegrationType, setSelectedIntegrationType] = useState<IEftba011>({} as IEftba011);

	const actionButtonDelete = (row: IEftba011) => {
		confirm(
			translate("Exclusão de Integração"),
			`${translate("Deseja realmente excluir a integração")}: ${row.efa011Descricao} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Integrations/Delete",
					urlParameters: {
						id: row.efa011Id
					}
				});

				addToast({
					color: "green",
					description: translate("Integração excluída com sucesso"),
					title: translate("Sucesso")
				});
				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir integração"), addToast);
			},
			true
		);
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange ,
		control: controlAddOrChange
	} = useForm();

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();


			const dataChanged = data;
			dataChanged.efa011Ativo = data.efa011Ativo ? "S" : "N";
			dataChanged.eftba021 = extractSingleValueFromArrayOfObject("value", data.eftba021);

			await addAndChangeSchema(translate).validate(data, {
				abortEarly: false
			});
			
			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Integrations/Insert",
					body: {
						...data
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Integrations/Update",
					body: {
						...data,
						efa011Id: selectedIntegrationType.efa011Id
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Integração adicionada com sucesso") : translate("Integração alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();
		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar integração") : translate("Erro ao alterar tipo de integração"), addToast);
		}
	};

	const queryIntegrationData = async (id: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Integrations/Select",
				urlParameters: {
					id
				}
			});
			setSelectedIntegrationType(data);
			modalAddOrChange.current?.openModal();
			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};

	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedIntegrationType({efa011Ativo: "S" } as IEftba011); 
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IEftba011) => {
		resetAddOrChange(); 
		setIsAdd(false); 
		queryIntegrationData(row.efa011Id);
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	return (
		<>	
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Integração") : translate("Alterar integração"),
					subtitle: isAdd ? "" : `${translate("Integração")}: ${selectedIntegrationType.efa011Descricao}` ,
					icon: {
						name: isAdd ? "plus" : "pencil"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justify="flex-end"
					>
						<Audit data={selectedIntegrationType} type="pop"/>
					</RCCGrid>

					{!isAdd && (
						<RCCGrid
							container
							item
							xs={12}
							sm={10}
						>
							<RCCInput 
								register={registerAddOrChange}
								name="efa011Key"
								label={translate("Chave")}
								maxLength={20}
								defaultValue={selectedIntegrationType.efa011Key}
								error={errorsAddOrChange.efa011Key}
								disabled
								required
							/>
						</RCCGrid>
					)}

					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="efa011Descricao"
							label={translate("Descrição")}
							maxLength={20}
							defaultValue={selectedIntegrationType.efa011Descricao}
							error={errorsAddOrChange.efa011Descricao}
							required
						/>
					</RCCGrid>

					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<LovDataOrigin 
							control={controlAddOrChange}
							name="eftba021"
							label={translate("Origem de dados para integração")}
							defaultValue={Array.isArray(selectedIntegrationType.eftba021) ? selectedIntegrationType.eftba021.join(", ") : selectedIntegrationType.eftba021}
							error={errorsAddOrChange.eftba021}
							isMulti
							required
						/>
						{errorsAddOrChange.eftba021 && errorsAddOrChange.eftba021.message && (
							<Title color="red" size="xs">
								<RCCIcon
									name="infoCircle"
									color="var(--red)"
									style={{ marginRight: "8px"}}
								/>
								{errorsAddOrChange.eftba021.message}
							</Title>
						)}
					</RCCGrid>

					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCSwitch 
							label={translate("Situação")} 
							name="efa011Ativo" 
							defaultChecked={selectedIntegrationType.efa011Ativo === "S" || isAdd} 
							error={errorsAddOrChange.efa011Ativo}
							register={registerAddOrChange}
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(Modals);
